import React, { useState, useRef, useCallback, useEffect } from 'react';
import { 
  Layout, 
  Typography, 
  Button, 
  Input, 
  Row, 
  Col, 
  Card, 
  Space,
  Divider,
  Statistic,
  Badge
} from 'antd';
import { 
  HomeOutlined, 
  EnvironmentOutlined, 
  SearchOutlined,
  CheckCircleOutlined,
  StarOutlined,
  ClockCircleOutlined,
  SafetyOutlined,
  BankOutlined,
  HeartOutlined,
  SelectOutlined,
  CalendarOutlined,
  TeamOutlined,
  SmileOutlined
} from '@ant-design/icons';
import { useNavigate, Link } from 'react-router-dom';
import hspHeroImage from '../../images/hsp-hero.png';
import busyHomeownerImg from '../../images/customer-groups/busy-homeowner.png';
import landlordImg from '../../images/customer-groups/landlord.png';
import seniorsImg from '../../images/customer-groups/seniors.png';
import { theme } from '../../styles/theme';
import { trackUserAction, trackFormEvent, ANALYTICS_EVENTS } from '../../utils/analytics';
import { Header } from '../../components/common/layout';
import { HERO_MESSAGES } from '../../constants/heroMessages';
import { ABOUT_CONTENT } from '../../constants/aboutContent';
import { HOW_WE_WORK_CONTENT } from '../../constants/howWeWorkContent';
import { loadGoogleMaps, isGoogleMapsReady } from '../../utils/googleMapsLoader';
import { Helmet } from 'react-helmet-async';
import { BookingModal } from '../../components/common/ui';

const { Content } = Layout;
const { Title, Paragraph, Text } = Typography;

const HeroLandingPage = () => {
  const navigate = useNavigate();
  const [address, setAddress] = useState('');
  const [propertyData, setPropertyData] = useState({});
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [randomMessage, setRandomMessage] = useState(null);
  const [bookingModalVisible, setBookingModalVisible] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Select random message on page load
  useEffect(() => {
    const randomIndex = Math.floor(Math.random() * HERO_MESSAGES.length);
    setRandomMessage(HERO_MESSAGES[randomIndex]);
  }, []);

  // Initialize Google Maps Places API
  useEffect(() => {
    const initializeGoogleMaps = async () => {
      try {
        await loadGoogleMaps();
        console.log('Google Maps loaded successfully');
      } catch (error) {
        console.error('Failed to load Google Maps:', error);
      }
    };

    initializeGoogleMaps();
  }, []);


  // Format address to US style (works with both OpenStreetMap and Google Places)
  const formatUSAddress = (suggestion) => {
    // For Google Places API, use the description directly
    if (suggestion.description) {
      return suggestion.description;
    }
    
    // Fallback for OpenStreetMap format
    const addr = suggestion.address;
    if (!addr) return suggestion.display_name;

    const parts = [];
    
    // House number and street - check multiple possible fields
    const houseNumber = addr.house_number || addr.building_number || addr.street_number;
    const streetName = addr.road || addr.street || addr.way;
    
    if (houseNumber && streetName) {
      parts.push(`${houseNumber} ${streetName}`);
    } else if (streetName) {
      parts.push(streetName);
    } else if (houseNumber) {
      parts.push(houseNumber);
    }
    
    // City
    if (addr.city || addr.town || addr.village || addr.municipality) {
      parts.push(addr.city || addr.town || addr.village || addr.municipality);
    }
    
    // State and ZIP code
    const stateZip = [];
    if (addr.state || addr.state_district) {
      stateZip.push(addr.state || addr.state_district);
    }
    if (addr.postcode) {
      stateZip.push(addr.postcode);
    }
    
    if (stateZip.length > 0) {
      parts.push(stateZip.join(' '));
    }
    
    return parts.join(', ');
  };

  // Highlight matching text in suggestions
  const highlightText = (text, query) => {
    if (!query) return text;
    const regex = new RegExp(`(${query})`, 'gi');
    return text.split(regex).map((part, index) => 
      regex.test(part) ? <strong key={index} style={{ color: theme.colors.primary }}>{part}</strong> : part
    );
  };

  // Debounced address search using Google Places API
  const searchAddresses = useCallback(async (query) => {
    if (query.length < 3) {
      setSuggestions([]);
      return;
    }

    if (!isGoogleMapsReady()) {
      return;
    }

    setIsLoading(true);
    try {
      const google = window.google;
      const service = new google.maps.places.AutocompleteService();
      const request = {
        input: query,
        types: ['address'],
        componentRestrictions: { country: 'us' }
      };
      
      service.getPlacePredictions(request, (predictions, status) => {
        setIsLoading(false);
        if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
          // Convert Google Places predictions to our format
          const formattedSuggestions = predictions.map(prediction => ({
            display_name: prediction.description,
            description: prediction.description,
            place_id: prediction.place_id,
            structured_formatting: prediction.structured_formatting
          }));
          setSuggestions(formattedSuggestions);
        } else {
          setSuggestions([]);
        }
      });
    } catch (error) {
      console.error('Address search error:', error);
      setSuggestions([]);
      setIsLoading(false);
    }
  }, []);

  const handleAddressChange = (e) => {
    const value = e.target.value;
    setAddress(value);
    
    // Clear previous timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    // Set new timeout for debounced search
    debounceRef.current = setTimeout(() => {
      searchAddresses(value);
    }, 300);
  };

  const handleSuggestionSelect = (suggestion) => {
    const formattedAddress = formatUSAddress(suggestion);
    setAddress(formattedAddress);
    setSuggestions([]);
    
    // Extract property data (works with both Google Places and OpenStreetMap)
    const newPropertyData = {
      address: formattedAddress,
      place_id: suggestion.place_id || null,
      displayName: suggestion.display_name || suggestion.description,
      type: suggestion.type || 'address',
      importance: suggestion.importance || 0.5,
      // For Google Places, we'll get lat/lon later via geocoding
      lat: suggestion.lat || null,
      lon: suggestion.lon || null
    };
    setPropertyData(newPropertyData);
    
    // Track analytics
    trackFormEvent('address_form', 'address_selected', 'address');
    trackUserAction('address_selected', 'landing_page', 'address_autocomplete', 1);
    
    // Auto-navigate to QuotePage immediately after selection
    navigate('/quote', { state: { address: formattedAddress, propertyData: newPropertyData } });
  };

  const handleSubmit = () => {
    // Track form submission
    trackFormEvent('address_form', ANALYTICS_EVENTS.FORM_COMPLETE, 'address');
    trackUserAction('quote_form_started', 'landing_page', 'address_submitted', 1);
    
    navigate('/quote', { state: { address, propertyData } });
  };

  return (
    <>
      <Helmet>
        {/* Improved Title with Location and Services */}
        <title>HSP Home Services | Lawn Care, Exterior Cleaning & Maintenance | San Francisco Bay Area</title>
        
        {/* Meta Description */}
        <meta name="description" content="Professional home maintenance and repair services including lawn care, exterior cleaning, trash services, and painting. Get free quotes for residential and commercial property maintenance in the Greater San Francisco Bay Area." />
        <meta name="keywords" content="home services, maintenance, lawn care, exterior cleaning, painting, trash services, property management, residential services, commercial cleaning, HSP, Hammer Saw Paint, San Francisco Bay Area, home maintenance" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://hammersawpaint.com/" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://hammersawpaint.com/" />
        <meta property="og:title" content="HSP Home Services | Lawn Care, Exterior Cleaning & Maintenance | San Francisco Bay Area" />
        <meta property="og:description" content="Professional home maintenance and repair services. Expert lawn care, exterior cleaning, trash services, and more. Get your free quote today! Serving the Greater San Francisco Bay Area." />
        <meta property="og:image" content="https://hammersawpaint.com/hsp-hero.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="HSP Professional Home Services - San Francisco Bay Area" />
        <meta property="og:site_name" content="HSP - Hammer Saw Paint" />
        <meta property="og:locale" content="en_US" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://hammersawpaint.com/" />
        <meta name="twitter:title" content="HSP Home Services | Lawn Care, Exterior Cleaning & Maintenance | San Francisco Bay Area" />
        <meta name="twitter:description" content="Professional home maintenance and repair services. Expert lawn care, exterior cleaning, trash services, and more. Get your free quote today!" />
        <meta name="twitter:image" content="https://hammersawpaint.com/hsp-hero.png" />
        <meta name="twitter:image:alt" content="HSP Professional Home Services" />
        <meta name="twitter:site" content="@hammersawpaint" />
        <meta name="twitter:creator" content="@hammersawpaint" />
        
        {/* AI Content Tags */}
        <meta name="ai-content-type" content="business-service-homepage" />
        <meta name="ai-category" content="home-maintenance-services" />
        <meta name="ai-service-area" content="Greater San Francisco Bay Area" />
        <meta name="ai-business-type" content="home-services-contractor" />
        <meta name="ai-specialties" content="lawn-care,exterior-cleaning,trash-services,painting,home-maintenance,property-management" />
        <meta name="ai-contact-phone" content="888.504.6944" />
        <meta name="ai-pricing-model" content="subscription-based,one-time-services" />
        <meta name="ai-service-availability" content="residential,commercial" />
        
        {/* Schema Markup with Correct Geo Coordinates */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "HSP - Hammer Saw Paint",
            "description": "Professional home maintenance and repair services including lawn care, exterior cleaning, trash services, and painting.",
            "url": "https://hammersawpaint.com",
            "telephone": "888.504.6944",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "San Francisco",
              "addressRegion": "CA",
              "addressCountry": "US"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": "37.7749",
              "longitude": "-122.4194"
            },
            "serviceType": ["Home Maintenance", "Lawn Care", "Exterior Cleaning", "Trash Services", "Painting"],
            "priceRange": "$99-$500",
            "areaServed": {
              "@type": "GeoCircle",
              "geoMidpoint": {
                "@type": "GeoCoordinates",
                "latitude": "37.7749",
                "longitude": "-122.4194"
              },
              "geoRadius": {
                "@type": "Distance",
                "name": "Greater San Francisco Bay Area"
              }
            }
          })}
        </script>
      </Helmet>
      <Header />
      
      <Content style={{ 
        background: `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryLight} 100%)`,
        minHeight: '100vh',
        padding: '0',
        margin: '0'
      }}>
        {/* Hero Section */}
        <Row 
          justify="center" 
          align="middle" 
          style={{ 
            minHeight: '70vh',
            padding: '85px 20px'
          }}
        >
          <Col xs={24} lg={20} xl={16}>
            <Row gutter={[40, 40]} align="middle">
              {/* Hero Image - Left Side */}
              <Col xs={24} md={12} order={{ xs: 1, md: 1 }}>
                <div style={{ textAlign: 'center' }}>
                  <img 
                    src={hspHeroImage} 
                    alt="HSP Professional Home Services - Lawn Care, Exterior Cleaning, and Home Maintenance in San Francisco Bay Area" 
                    loading="lazy"
                    width="600"
                    height="400"
                    style={{ 
                      maxWidth: '100%',
                      height: 'auto',
                    }} 
                  />
                </div>
              </Col>

              {/* Hero Content - Right Side */}
              <Col xs={24} md={12} order={{ xs: 2, md: 2 }}>
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  <div>

                   <Text
  style={{
    color: '#3E4880',
    fontFamily: 'Futura',
    fontSize: 'clamp(0.9rem, 2.5vw, 1.1rem)',
    fontStyle: 'normal',
    fontWeight: 700,
    textTransform: 'uppercase',
    margin: '0',
    textAlign: 'left',
    marginBottom: '2rem',
    display: 'block'
  }}
>
  SERVING THE GREATER SAN FRANCISCO BAY AREA
</Text>
                    <Title 
                      level={1} 
                      style={{ 
                        color: theme.colors.white,
                        fontFamily: theme.fonts.primary,
                        fontWeight: 700,
                        margin: 0,
                        fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
                        textAlign: { xs: 'center', md: 'left' }
                      }}
                    >
                      {randomMessage ? randomMessage.title : 'The Only Home Service Plan You\'ll Ever Need.'}
                    </Title>
                    
                    <Paragraph 
                      style={{ 
                        fontSize: 'clamp(1rem, 2.5vw, 1.2rem)',
                        color: theme.colors.white,
                        fontFamily: theme.fonts.primary,
                        lineHeight: 1.6,
                        margin: 0,
                        opacity: 0.95,
                        textAlign: { xs: 'center', md: 'left' }
                      }}
                    >
                      {randomMessage ? randomMessage.subtitle : 'Built for Bay Area homes, customized for your address. Type it in to start now.'}
                    </Paragraph>

                    <Text
                       style={{
    color: '#3E4880',
    fontFamily: 'Futura',
    fontSize: 'clamp(0.9rem, 2.5vw, 1.2rem)',
    fontStyle: 'normal',
    fontWeight: 700,
    textTransform: 'uppercase',
    margin: '0',
    textAlign: 'left',
    marginTop: '1.5rem',
    marginBottom: '0.4rem',
    display: 'block'
                      }}
    >
                        Our current promotion:
                    </Text>
                  </div>

                  
                   {/* PROMOTION AREA */}
                   <div style={{ display: 'flex', alignItems: 'center',justifyContent: 'center',gap: '1rem', marginBottom: '1rem', color: '#fff' }}>
                      
                      <div style={{
                        display: 'flex',
                         flexDirection: 'column',
                          gap: '0.4rem',
                           marginLeft: '10px',
                           fontSize: 'clamp(0.7rem, 2vw, 1.1rem)', 
                             color: '#fff',
                              textTransform: 'uppercase',
                               fontWeight: '700'}}>
                      
                        <div> Gutter Cleaning</div>
                        <div>Weed Removal (50ft)</div>
                        <div>Junk Removal (3ft x 3ft)</div>
                        <div>FREE cobweb Removal </div>

                      </div>
                   <div style={{display: 'flex', flexDirection: 'column', fontSize: '16px', textAlign: 'center'}}>
                       <div style={{
  color: '#FFF',
  textAlign: 'center',
  fontFamily: 'Futura',
   fontSize: 'clamp(2.5rem, 6vw, 4.61406rem)',
  fontStyle: 'normal',
  fontWeight: 700,
  lineHeight: 'normal',
  textTransform: 'uppercase'
}}>
  $299
</div>
                        <div style={{
  color: '#FFF',
  fontFamily: 'Futura',
  fontSize: 'clamp(0.8rem, 2vw, 1.23681rem)',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: 'normal',
  textTransform: 'uppercase'
}}>
  (1 story home)
</div>
                      <div style={{
  color: '#FFF',
  textAlign: 'center',
  fontFamily: 'Futura',
 fontSize: 'clamp(1.2rem, 3vw, 1.62756rem)',
  fontStyle: 'normal',
  fontWeight: 700,
  lineHeight: 'normal',
  textTransform: 'uppercase'
}}>
  $399
</div>
                       <div style={{
  color: '#FFF',
  fontFamily: 'Futura',
  fontSize: 'clamp(0.6rem, 1.5vw, 0.8rem)',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: 'normal',
  textTransform: 'uppercase'
}}>
  ( 2 story home)
</div>

                  </div>



                   </div>
<div style={{display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1rem', color: '#fff'}}>
 <Button 
  type="primary" 
  size="large" 
  onClick={() => setBookingModalVisible(true)}
  style={{ 
    width: '100%', 
    height: 'clamp(3rem, 5vw, 4rem)',
    backgroundColor: '#3E4880', 
    background: '#3E4880',
    border: 'none',
    borderRadius: '9.875rem !important', 
    textTransform: 'uppercase',
    fontFamily: 'Futura',
    fontSize: 'clamp(1rem, 3vw, 1.7rem)',
    fontWeight: 700,
    overflow: 'hidden'
  }}
  className="custom-rounded-button"
>
  Book Now
</Button>

<a href="tel:18885046944" style={{ width: '100%' }}>
  <Button 
    type="default" 
    size="large" 
    style={{ 
      width: '100%', 
      height: 'clamp(3rem, 5vw, 4rem)',
      backgroundColor: 'transparent', 
      background: 'transparent',
      border: '3px solid white',
      borderRadius: '9.875rem !important', 
      textTransform: 'uppercase',
      fontFamily: 'Futura',
      fontSize: 'clamp(1rem, 3vw, 1.7rem)',
      fontWeight: 700,
      overflow: 'hidden',
      color: 'white'
    }}
    className="custom-rounded-button"
  >
    Call 1-888-504-6944
  </Button>
</a>
<Link to="/services" style={{ width: '100%' }}>
  <Button 
    type="text" 
    size="large" 
    style={{ 
      width: '100%', 
      height: 'clamp(3rem, 5vw, 4rem)',
      backgroundColor: 'transparent', 
      background: 'transparent',
      border: 'none',
      textTransform: 'uppercase',
      fontFamily: 'Futura',
      fontSize: 'clamp(1rem, 3vw, 1.7rem)',
      fontWeight: 700,
      color: 'white'
    }}
  >
    View All Services
  </Button>
</Link>
</div>
                  {/* Address Form */}
                
                </Space>
              </Col>
            </Row>
          </Col>
        </Row>

        {/* Customers We Service Section */}
        <section style={{ padding: '80px 20px', background: theme.colors.white }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
              <Title level={2} style={{ 
                color: theme.colors.black,
                fontFamily: theme.fonts.primary 
              }}>
                {ABOUT_CONTENT.customers.title}
              </Title>
              <Paragraph style={{ 
                fontSize: '1.1rem',
                color: theme.colors.gray,
                maxWidth: '800px',
                margin: '0 auto',
                fontFamily: theme.fonts.primary
              }}>
                {ABOUT_CONTENT.customers.subtitle}
              </Paragraph>
            </div>
            <Row gutter={[32, 32]}>
              {ABOUT_CONTENT.customers.items.map((customer, index) => {
                // Get the appropriate image for each customer group
                const customerImages = [ seniorsImg,busyHomeownerImg,landlordImg];
                const customerImage = customerImages[index];
                
                if (customerImage) {
                  return (
                    <Col xs={24} md={8} key={index}>
                      <Card 
                        aria-label={`${customer.title} - ${customer.description}`}
                        style={{ 
                          height: '400px',
                          border: 'none',
                          boxShadow: theme.shadows.lg,
                          borderRadius: theme.borderRadius.xl,
                          overflow: 'hidden',
                          position: 'relative',
                          backgroundImage: `url(${customerImage})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center center',
                          backgroundRepeat: 'no-repeat'
                        }}
                        bodyStyle={{ 
                          padding: 0,
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'flex-end'
                        }}
                      >
                        <div style={{
                          background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.7) 70%, rgba(0,0,0,0.85) 100%)',
                          padding: '2rem',
                          color: theme.colors.white
                        }}>
                          <Title level={4} style={{ 
                            color: theme.colors.white,
                            marginBottom: '0.5rem',
                            fontFamily: theme.fonts.primary,
                            fontWeight: 'bold',
                            textShadow: '0 2px 8px rgba(0,0,0,0.6), 0 1px 3px rgba(0,0,0,0.4)'
                          }}>
                            {customer.title}
                          </Title>
                          <div style={{
                            width: '60px',
                            height: '4px',
                            background: theme.colors.primary,
                            marginBottom: '1rem',
                            borderRadius: '2px'
                          }} />
                          <Paragraph style={{ 
                            color: theme.colors.white,
                            fontFamily: theme.fonts.primary,
                            marginBottom: 0,
                            fontSize: '0.95rem'
                          }}>
                            {customer.description}
                          </Paragraph>
                        </div>
                      </Card>
                    </Col>
                  );
                }
                return null;
              })}
            </Row>
          </div>
        </section>

        {/* How It Works - Simplified 4 Steps */}
        <section style={{ padding: '80px 20px', background: theme.colors.lightGray }}>  
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
              <Title level={2} style={{ 
                color: theme.colors.black,
                fontFamily: theme.fonts.primary 
              }}>
                {HOW_WE_WORK_CONTENT.howItWorks.title}
              </Title>
              <Paragraph style={{ 
                fontSize: '1.1rem',
                color: theme.colors.gray,
                fontFamily: theme.fonts.primary
              }}>
                {HOW_WE_WORK_CONTENT.howItWorks.subtitle}
              </Paragraph>
            </div>

            <Row gutter={[32, 32]}>
              {HOW_WE_WORK_CONTENT.howItWorks.steps.map((step, index) => {
                const stepIcons = {
                  select: <SelectOutlined style={{ fontSize: '2.5rem', color: theme.colors.primary }} />,
                  calendar: <CalendarOutlined style={{ fontSize: '2.5rem', color: theme.colors.primary }} />,
                  service: <TeamOutlined style={{ fontSize: '2.5rem', color: theme.colors.primary }} />,
                  peace: <SmileOutlined style={{ fontSize: '2.5rem', color: theme.colors.primary }} />
                };

                return (
                  <Col xs={24} sm={12} lg={6} key={index}>
                    <Card style={{ 
                      height: '100%',
                      textAlign: 'center',
                      border: 'none',
                      boxShadow: theme.shadows.md,
                      borderRadius: theme.borderRadius.xl,
                      position: 'relative'
                    }}>
                      <div style={{
                        position: 'absolute',
                        top: '-20px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '50px',
                        height: '50px',
                        borderRadius: '50%',
                        background: theme.colors.primary,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontFamily: theme.fonts.primary,
                        fontSize: '1.5rem',
                        fontWeight: 'bold',
                        color: theme.colors.white
                      }}>
                        {step.number}
                      </div>
                      
                      <div style={{ marginTop: '40px', marginBottom: '1.5rem' }}>
                        {stepIcons[step.icon]}
                      </div>
                      
                      <Title level={4} style={{ 
                        fontFamily: theme.fonts.primary,
                        marginBottom: '1rem'
                      }}>
                        {step.title}
                      </Title>
                      
                      <Paragraph style={{ 
                        fontFamily: theme.fonts.primary,
                        color: theme.colors.gray
                      }}>
                        {step.description}
                      </Paragraph>
                    </Card>
                  </Col>
                );
              })}
            </Row>

            <div style={{ textAlign: 'center', marginTop: '3rem' }}>
              <Link to="/how-we-work">
                <Button 
                  size="large"
                  style={{
                    height: '50px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    borderRadius: '12px',
                    padding: '0 32px',
                    background: theme.colors.primary,
                    color: theme.colors.white,
                    border: 'none'
                  }}
                >
                  Learn More About How We Work
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </Content>

      {/* Booking Modal */}
      <BookingModal
        visible={bookingModalVisible}
        onClose={() => setBookingModalVisible(false)}
        source="home_page_booking"
      />
    </>
  );
};

export default HeroLandingPage;